import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { ArrowLeft, Save, Check, Download, Package, Loader2 } from 'lucide-react';

import API_URL from '@/config/api';
import PaymentTermsSelector from '@/components/PaymentTermsSelector';
import logger from '@/utils/logger';

const StepReview = ({
  projectType,
  customerDetails,
  totals,
  additionalCosts,
  wallingFencingRequired,
  savedQuoteRef,
  isSaving,
  onBack,
  onSave,
  surfacingItems,
  edgings,
  wallingFencingItems,
  miscItems,
  grandTotal,
  paymentTerms,
  onPaymentTermsChange,
  onPaymentTermsValidityChange,
  paymentTermsValid = true,
  showVat = false,
  onShowVatChange,
  onGetMaterialPrice,
  onOrderMaterials,
  materialLink,
}) => {
  const navigate = useNavigate();
  const [ordering, setOrdering] = useState(false);
  const materialOrdered = !!materialLink?.material_quote_id && materialLink?.material_status && materialLink.material_status !== 'draft';
  const placeOrder = async () => {
    setOrdering(true);
    try { await onOrderMaterials?.(); } finally { setOrdering(false); }
  };
  const materialTotal = materialLink?.material_total_ex_vat ?? materialLink?.material_total ?? null;
  const netForMargin = totals?.grandTotal || grandTotal || 0;
  const margin = materialTotal != null ? netForMargin - materialTotal : null;
  const marginPct = margin != null && netForMargin > 0 ? (margin / netForMargin) * 100 : null;
  const [isGeneratingPDF, setIsGeneratingPDF] = useState(false);

  // Prices in state are ex-VAT (trade prices). UK VAT is 20%.
  const VAT_RATE = 0.20;
  const netTotal = totals?.grandTotal || 0;
  const vatAmount = +(netTotal * VAT_RATE).toFixed(2);
  const grossTotal = +(netTotal * (1 + VAT_RATE)).toFixed(2);

  const handleDownloadPDF = async () => {
    setIsGeneratingPDF(true);
    try {
      const today = new Date();
      const quoteDate = `${today.getFullYear()}-${String(today.getMonth()+1).padStart(2,'0')}-${String(today.getDate()).padStart(2,'0')}`;

      const payload = {
        customer_name: customerDetails.customerName || null,
        customer_address: customerDetails.customerAddress || null,
        customer_phone: customerDetails.customerNumber || null,
        installer_name: customerDetails.yourName || null,
        company_name: customerDetails.companyName || null,
        installer_phone: customerDetails.yourNumber || null,
        project_type: projectType,
        quote_date: quoteDate,
        surfacing_items: surfacingItems || [],
        additional_costs: additionalCosts || {},
        edgings: edgings || [],
        walling_fencing_items: wallingFencingItems || [],
        misc_items: miscItems || [],
        totals: totals || {},
        show_vat: !!showVat,
      };
      
      const response = await fetch(`${API_URL}/api/quotes/generate-pdf`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(payload)
      });
      
      if (response.ok) {
        const blob = await response.blob();
        const disposition = response.headers.get('Content-Disposition');
        const filename = disposition ? disposition.split('filename=')[1] : 'quote.pdf';
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = filename;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        window.URL.revokeObjectURL(url);
      } else {
        alert('Error generating PDF. Please try again.');
      }
    } catch (error) {
      logger.error('Error generating PDF:', error);
      alert('Error generating PDF. Please try again.');
    } finally {
      setIsGeneratingPDF(false);
    }
  };

  return (
    <div className="bg-white rounded-2xl shadow-lg border border-gray-100 p-6 md:p-8">
      <h2 className="text-xl md:text-2xl font-bold text-[#27292E] mb-2">
        Review Your Quote
      </h2>
      <p className="text-gray-600 mb-6">
        Review the details below before saving your quote.
      </p>

      {/* Quote Summary */}
      <div className="space-y-6">
        {/* Project Info */}
        <div className="p-4 bg-gray-50 rounded-xl">
          <h3 className="font-bold text-[#27292E] mb-3">Project Information</h3>
          <div className="space-y-2 text-sm">
            <div className="flex justify-between">
              <span className="text-gray-600">Project Type:</span>
              <span className="font-medium">
                {projectType === 'resin_bound' ? 'Resin Bound Stone' : projectType}
              </span>
            </div>
            {customerDetails.customerName && (
              <div className="flex justify-between">
                <span className="text-gray-600">Customer:</span>
                <span className="font-medium">{customerDetails.customerName}</span>
              </div>
            )}
            {customerDetails.customerAddress && (
              <div className="flex justify-between">
                <span className="text-gray-600">Address:</span>
                <span className="font-medium text-right max-w-[60%]">{customerDetails.customerAddress}</span>
              </div>
            )}
          </div>
        </div>

        {/* Installer Info */}
        {(customerDetails.yourName || customerDetails.companyName) && (
          <div className="p-4 bg-gray-50 rounded-xl">
            <h3 className="font-bold text-[#27292E] mb-3">Installer Details</h3>
            <div className="space-y-2 text-sm">
              {customerDetails.yourName && (
                <div className="flex justify-between">
                  <span className="text-gray-600">Name:</span>
                  <span className="font-medium">{customerDetails.yourName}</span>
                </div>
              )}
              {customerDetails.companyName && (
                <div className="flex justify-between">
                  <span className="text-gray-600">Company:</span>
                  <span className="font-medium">{customerDetails.companyName}</span>
                </div>
              )}
              {customerDetails.yourNumber && (
                <div className="flex justify-between">
                  <span className="text-gray-600">Contact:</span>
                  <span className="font-medium">{customerDetails.yourNumber}</span>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Cost Breakdown */}
        <div className="p-4 bg-gray-50 rounded-xl">
          <h3 className="font-bold text-[#27292E] mb-3">Cost Breakdown</h3>
          <div className="space-y-3">
            {/* Surfacing */}
            {totals.itemTotals.length > 0 && (
              <div>
                <h4 className="text-sm font-medium text-gray-700 mb-2">Surfacing</h4>
                {totals.itemTotals.map((item) => (
                  <div key={`surf-${item.name}`} className="flex justify-between text-sm py-1">
                    <span className="text-gray-600">
                      {item.name.length > 40 ? item.name.substring(0, 40) + '...' : item.name}
                      <span className="text-gray-400 ml-1">({item.squareMetres}m²)</span>
                    </span>
                    <span className="font-medium">£{item.subtotal.toLocaleString('en-GB', { minimumFractionDigits: 2 })}</span>
                  </div>
                ))}
                <div className="flex justify-between text-sm pt-2 border-t border-gray-200 mt-2">
                  <span className="font-medium text-gray-700">Surfacing Subtotal:</span>
                  <span className="font-bold text-[#300122]">£{totals.surfacingTotal.toLocaleString('en-GB', { minimumFractionDigits: 2 })}</span>
                </div>
              </div>
            )}

            {/* Additional Costs */}
            {totals.additionalTotal > 0 && (
              <div className="pt-3 border-t border-gray-200">
                <h4 className="text-sm font-medium text-gray-700 mb-2">Additional Costs</h4>
                {additionalCosts.diggerRequired && (
                  <div className="flex justify-between text-sm py-1">
                    <span className="text-gray-600">Digger</span>
                    <span className="font-medium">£{additionalCosts.diggerCost.toLocaleString('en-GB', { minimumFractionDigits: 2 })}</span>
                  </div>
                )}
                {additionalCosts.skipRequired && (
                  <div className="flex justify-between text-sm py-1">
                    <span className="text-gray-600">Skip</span>
                    <span className="font-medium">£{additionalCosts.skipCost.toLocaleString('en-GB', { minimumFractionDigits: 2 })}</span>
                  </div>
                )}
                {additionalCosts.loadAwayCount > 0 && (
                  <div className="flex justify-between text-sm py-1">
                    <span className="text-gray-600">Load Away ({additionalCosts.loadAwayCount} loads)</span>
                    <span className="font-medium">£{totals.loadAwayTotal.toLocaleString('en-GB', { minimumFractionDigits: 2 })}</span>
                  </div>
                )}
              </div>
            )}

            {/* Edgings */}
            {totals.edgingsTotal > 0 && (
              <div className="pt-3 border-t border-gray-200">
                <h4 className="text-sm font-medium text-gray-700 mb-2">Edgings</h4>
                {totals.edgingTotals.map((item) => (
                  <div key={`edge-${item.name}`} className="flex justify-between text-sm py-1">
                    <span className="text-gray-600">
                      {item.name}
                      <span className="text-gray-400 ml-1">({item.quantity} {item.unit === 'm' ? 'm' : 'units'})</span>
                    </span>
                    <span className="font-medium">£{item.subtotal.toLocaleString('en-GB', { minimumFractionDigits: 2 })}</span>
                  </div>
                ))}
              </div>
            )}

            {/* Walling & Fencing */}
            {totals.wallingFencingTotal > 0 && (
              <div className="pt-3 border-t border-gray-200">
                <h4 className="text-sm font-medium text-gray-700 mb-2">Walling & Fencing</h4>
                {totals.wallingFencingTotals.map((item) => (
                  <div key={`wall-${item.name}`} className="flex justify-between text-sm py-1">
                    <span className="text-gray-600">
                      {item.name}
                      {item.unit === 'm²' && item.calculatedArea > 0 && (
                        <span className="text-gray-400 ml-1">({item.calculatedArea.toFixed(2)} m²)</span>
                      )}
                      {item.unit !== 'm²' && (
                        <span className="text-gray-400 ml-1">({item.quantity} {item.unit})</span>
                      )}
                    </span>
                    <span className="font-medium">£{item.subtotal.toLocaleString('en-GB', { minimumFractionDigits: 2 })}</span>
                  </div>
                ))}
              </div>
            )}

            {/* Miscellaneous */}
            {totals.miscTotal > 0 && (
              <div className="pt-3 border-t border-gray-200">
                <h4 className="text-sm font-medium text-gray-700 mb-2">Miscellaneous Items</h4>
                {totals.miscTotals.map((item) => (
                  <div key={`misc-${item.name}`} className="flex justify-between text-sm py-1">
                    <span className="text-gray-600">
                      {item.name}
                      <span className="text-gray-400 ml-1">({item.quantity})</span>
                    </span>
                    <span className="font-medium">£{item.subtotal.toLocaleString('en-GB', { minimumFractionDigits: 2 })}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* VAT display toggle */}
        <div
          className="flex items-center justify-between p-4 bg-white border-2 border-gray-200 rounded-xl"
          data-testid="quote-vat-toggle-row"
        >
          <div>
            <p className="text-sm font-semibold text-[#27292E]">Show VAT breakdown</p>
            <p className="text-xs text-gray-500 mt-0.5">
              {showVat
                ? 'Displays Subtotal, VAT @ 20% and Total inc. VAT — useful for trade / VAT-registered customers.'
                : 'Displays a single VAT-inclusive figure with no mention of VAT — recommended for retail customers.'}
            </p>
          </div>
          <label className="inline-flex items-center cursor-pointer flex-shrink-0 ml-4">
            <input
              type="checkbox"
              className="sr-only peer"
              checked={!!showVat}
              onChange={(e) => onShowVatChange?.(e.target.checked)}
              data-testid="quote-show-vat-toggle"
            />
            <div className="relative w-11 h-6 bg-gray-300 rounded-full peer-checked:bg-[#300122] transition-colors after:content-[''] after:absolute after:top-0.5 after:left-0.5 after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-transform peer-checked:after:translate-x-5"></div>
          </label>
        </div>

        {/* Grand Total */}
        <div className="p-6 bg-[#300122] text-white rounded-xl" data-testid="quote-grand-total-block">
          {showVat ? (
            <div className="space-y-2">
              <div className="flex justify-between text-sm text-white/85">
                <span>Subtotal (Excl. VAT)</span>
                <span data-testid="quote-net-total">£{netTotal.toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
              </div>
              <div className="flex justify-between text-sm text-white/85">
                <span>VAT @ 20%</span>
                <span data-testid="quote-vat-amount">£{vatAmount.toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
              </div>
              <div className="flex justify-between items-center pt-2 border-t border-white/25">
                <span className="text-sm font-semibold">Total (Incl. VAT)</span>
                <span className="text-2xl sm:text-3xl font-bold" data-testid="quote-gross-total">
                  £{grossTotal.toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                </span>
              </div>
            </div>
          ) : (
            <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-2">
              <p className="text-white/80 text-sm">Total</p>
              <p className="text-3xl sm:text-4xl font-bold" data-testid="quote-gross-total">
                £{grossTotal.toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
              </p>
            </div>
          )}
        </div>

        {/* Success Message */}
        {savedQuoteRef && (
          <div className="p-4 bg-green-50 border border-green-200 rounded-xl">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-green-500 rounded-full flex items-center justify-center flex-shrink-0">
                <Check className="w-6 h-6 text-white" />
              </div>
              <div>
                <p className="font-bold text-green-800">Quote Saved Successfully!</p>
                <p className="text-sm text-green-600">Reference: {savedQuoteRef}</p>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Ox material price — link to the material calculator, pre-filled from this quote */}
      {onGetMaterialPrice && (
        <div className="mt-6 rounded-xl border-2 border-[#2F5E96]/30 bg-[#2F5E96]/5 p-4" data-testid="material-price-card">
          <div className="flex flex-col sm:flex-row sm:items-center gap-3">
            <div className="flex-1 min-w-0">
              <p className="font-bold text-[#27292E] flex items-center gap-2"><Package className="w-4 h-4 text-[#2F5E96]" /> Ox material price</p>
              {materialTotal != null ? (
                <>
                  <p className="text-sm text-gray-700 mt-0.5" data-testid="material-price-total">
                    Materials <strong>£{Number(materialTotal).toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</strong> ex-VAT{materialLink?.material_quote_ref ? ` · ${materialLink.material_quote_ref}` : ''}
                    {materialOrdered
                      ? <span className="ml-2 inline-flex items-center gap-1 text-[11px] font-bold px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-800" data-testid="material-ordered-badge"><Check className="w-3 h-3" /> Ordered with Ox</span>
                      : <span className="ml-2 text-[11px] font-bold px-2 py-0.5 rounded-full bg-sky-100 text-sky-800" data-testid="material-draft-badge">Price only — not ordered</span>}
                  </p>
                  {margin != null && (
                    <p className={`text-sm mt-0.5 font-semibold ${margin < 0 ? 'text-rose-700' : marginPct < 30 ? 'text-amber-700' : 'text-emerald-700'}`} data-testid="material-margin">
                      Est. gross margin £{margin.toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ({marginPct.toFixed(0)}%) on this quote (before labour)
                    </p>
                  )}
                </>
              ) : (
                <p className="text-sm text-gray-600 mt-0.5">Uses these areas, blends and customer details to price the Ox materials in seconds — nothing is sent to the customer.</p>
              )}
            </div>
            <div className="flex flex-wrap gap-2 shrink-0">
              {materialLink?.material_quote_id && (
                <Button variant="outline" onClick={() => navigate(`/probound/orders/${materialLink.material_quote_id}`)} className="h-11 rounded-xl border-2 border-[#2F5E96] text-[#2F5E96]" data-testid="material-price-view">View</Button>
              )}
              {!materialOrdered && (
                <Button onClick={onGetMaterialPrice} disabled={isSaving} variant={materialTotal != null ? 'outline' : 'default'} className={`h-11 rounded-xl font-bold ${materialTotal != null ? 'border-2 border-[#2F5E96] text-[#2F5E96]' : 'bg-[#2F5E96] hover:bg-[#1e3a5f] text-white'}`} data-testid="get-material-price-btn">
                  <Package className="w-4 h-4 mr-2" /> {materialTotal != null ? 'Re-price materials' : 'Get material price'}
                </Button>
              )}
              {materialLink?.material_quote_id && !materialOrdered && onOrderMaterials && (
                <Button onClick={placeOrder} disabled={ordering || isSaving} className="h-11 rounded-xl bg-emerald-600 hover:bg-emerald-700 text-white font-bold" data-testid="order-materials-btn" title="Places a confirmed order with Ox Resin for these materials and marks this quote as accepted by the customer">
                  {ordering ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Check className="w-4 h-4 mr-2" />} Order these materials
                </Button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Actions */}
      {!savedQuoteRef && (
        <>
          <div className="mb-5">
            <PaymentTermsSelector
              grandTotal={grandTotal || totals?.grandTotal || 0}
              value={paymentTerms}
              onChange={onPaymentTermsChange}
              onValidityChange={onPaymentTermsValidityChange}
              serviceType="probound"
            />
          </div>
          <div className="flex flex-col sm:flex-row gap-3 mt-8">
            <Button
              onClick={onBack}
              variant="outline"
              className="flex-1 h-12 sm:h-14 text-base sm:text-lg font-medium rounded-xl border-2 border-[#300122] text-[#300122] hover:bg-[#300122]/5"
            >
              <ArrowLeft className="w-5 h-5 mr-2 flex-shrink-0" />
              <span className="truncate">Back</span>
            </Button>
            <Button
              onClick={() => onSave(false)}
              disabled={isSaving || !paymentTermsValid}
              variant="outline"
              className="flex-1 h-12 sm:h-14 text-base sm:text-lg font-medium rounded-xl border-2 border-[#300122] text-[#300122] hover:bg-[#300122]/5 disabled:opacity-50"
              data-testid="save-draft-btn"
            >
              {isSaving ? <>Saving…</> : <><Save className="w-5 h-5 mr-2 flex-shrink-0" /><span className="truncate">Save Draft</span></>}
            </Button>
            <Button
              onClick={() => onSave(true)}
              disabled={isSaving || !paymentTermsValid}
              className="flex-1 bg-[#300122] hover:bg-[#4a0636] text-white h-12 sm:h-14 text-base sm:text-lg font-bold rounded-xl disabled:opacity-50"
              data-testid="save-and-send-btn"
            >
              {isSaving ? <>Sending…</> : <><Check className="w-5 h-5 mr-2 flex-shrink-0" /><span className="truncate">Save &amp; Send</span></>}
            </Button>
          </div>
          <div className="mt-4">
            <Button
              onClick={handleDownloadPDF}
              disabled={isGeneratingPDF}
              variant="outline"
              className="w-full h-12 text-base font-medium rounded-xl border-2 border-[#2F5E96] text-[#2F5E96] hover:bg-[#2F5E96]/5"
              data-testid="download-pdf-btn"
            >
              {isGeneratingPDF ? (
                <>Generating PDF...</>
              ) : (
                <>
                  <Download className="w-5 h-5 mr-2 flex-shrink-0" />
                  <span>Download PDF Quote</span>
                </>
              )}
            </Button>
          </div>

          <p className="text-center text-sm text-gray-500 mt-4">
            {customerDetails.customerName 
              ? 'This quote will be saved and linked to the customer as a new lead.'
              : 'Enter a customer name in Step 6 to create a lead, or save without to keep as a standalone quote.'}
          </p>
        </>
      )}

      {/* Download PDF after save */}
      {savedQuoteRef && (
        <div className="mt-6">
          <Button
            onClick={handleDownloadPDF}
            disabled={isGeneratingPDF}
            className="w-full h-14 text-base sm:text-lg font-bold rounded-xl bg-[#2F5E96] hover:bg-[#1e3a5f] text-white"
            data-testid="download-pdf-after-save-btn"
          >
            {isGeneratingPDF ? (
              <>Generating PDF...</>
            ) : (
              <>
                <Download className="w-5 h-5 mr-2 flex-shrink-0" />
                <span>Download PDF Quote</span>
              </>
            )}
          </Button>
        </div>
      )}
    </div>
  );
};

export default StepReview;
